import React, {useEffect, useRef, useState} from 'react';
import {RouteComponentProps, useHistory} from 'react-router-dom';
import {
    IonAlert,
    IonButtons,
    IonCol,
    IonGrid,
    IonHeader,
    IonPage, IonRow, isPlatform,
    useIonViewWillEnter,
    useIonViewWillLeave
} from '@ionic/react';
import {
    StyledEmptyContainer,
    StyledIonContentCameraPreview,
    StyledIonTitle,
    StyledIonToolbar
} from '../../components/content/content.style';
import {StyledButton, StyledHeaderButton, StyledHeaderButtonImage} from '../../components/button/button.style';
import {useTranslation} from 'react-i18next';
import {StyledContent} from '../takePhotoPage/takePhotoPage.style';
import CircleImage, {ImageTypes} from "../../components/circleImage/circleImage.component";
import {Links} from "../links";
import {
    checkSpecification as checkSpecificationFactory,
    findOrderById
} from "../../services/reportFactory.service";

import {
    CameraPreviewImage
} from "../../utils/styles/icons";
import ArrowLeftImage from '../../assets/images/arrow-left.svg';

import {
    checkCameraPermission,
    openSettings,
    startContinuousScan,
    makeWebViewTransparent,
    restoreWebView,
    BARCODE_QR_FORMATS,
    ContinuousScanHandle,
} from "@services/barcodeScanner.service";

interface RouterProps {
    internalCostReport?: string;
}

interface PageProps extends RouteComponentProps<RouterProps> {
}

const QRCodePage: React.FC<PageProps> = ({match}) => {
    const {params: {internalCostReport}} = match;

    const {t} = useTranslation();
    const history = useHistory();

    const handleBack = () => {
        history.goBack();
    };

    const [topEdge, updateTopEdge] = useState<number | undefined>(undefined);
    const [showError, setShowError] = useState<boolean>(false);
    const [error, setError] = useState<string>();
    const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);

    const scanHandleRef = useRef<ContinuousScanHandle | null>(null);
    // MLKit's 'barcodesScanned' fires once per camera frame with no built-in
    // debounce, so the same code in view triggers this callback many times in a
    // row. Without claiming it synchronously on the first hit, each of those
    // concurrent calls independently resolves and calls history.push(), stacking
    // several duplicate report entries in history before the camera even stops -
    // which is why going back/discard appeared to do nothing: it just popped one
    // duplicate for another, identical-looking one.
    const handledRef = useRef(false);

    useEffect(() => {
        updateHeight();
    })

    const startScan = async () => {
        makeWebViewTransparent();
        handledRef.current = false;

        const handle = await startContinuousScan(BARCODE_QR_FORMATS, async (rawValue) => {
            if (rawValue && !handledRef.current) {
                handledRef.current = true;
                let isOrder = rawValue.includes('order_');
                let error = await handleScan(rawValue.replace('order_', ''), isOrder);
                if (typeof error == 'string') {
                    handledRef.current = false;
                    if (error == 'SPEC_NO_NEWEST') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.spec_no_newest'))
                    } else if (error == 'SPEC_NO_ACCEPTED') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.spec_no_accepted'))
                    } else if (error == 'ORDER_TYPE_NOT_ALLOWED') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.order_type_not_allowed'))
                    } else if (error == 'ORDER_NOT_FOUND') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.order_not_found'))
                    } else if (error == 'ORDER_BLOCKED') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.order_blocked'))
                    } else if (error == 'ORDER_CLOSED') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.order_closed'))
                    } else if (error == 'WORKER_NO_ALLOWED') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.worker_no_allowed'))
                    } else if (error == 'WORKER_REPORTING_BLOCKED') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.worker_reporting_blocked'))
                    } else if (error == 'CLIENT_REPORTING_NOT_ENABLE') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.client_reporting_not_enable'))
                    } else if (error == 'ORDER_LIMITS_EXCEEDED') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.order_limits_exceeded'))
                    } else if (error == 'ORDER_APT_NOT_ALLOWED') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.order_apt_not_allowed'))
                    } else if (error == 'ORDER_BLOCKED_BY_STATUS') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.order_blocked_by_status'))
                    } else if (error == 'WORKER_NOT_ENABLED') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.worker_not_enabled'))
                    } else if (error == 'WORKER_WITHOUT_AVAILABILITIES') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.worker_without_availability'))
                    } else if (error == 'WORKER_WITHOUT_PROPOSALS') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.worker_without_proposals'))
                    } else if (error == 'LOCALIZATION_NOT_AVAILABLE') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.localization_not_available'))
                    } else if (error == 'LOCALIZATION_CLIENT_NOT_MATCHING') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.localization_client_not_matching'))
                    } else if (error == 'TL_REPORTING_BLOCKED') {
                        setError(t('reportsTab.orders.scanSpecificationPage.error.tl_reporting_blocked'))
                    } else {
                        if (isOrder) {
                            setError(t('reportsTab.orders.manualOrderFind.error.common'))
                        } else {
                            setError(t('reportsTab.orders.scanSpecificationPage.error.common'))
                        }
                    }

                    stopScan();
                    setShowError(true);
                }
            }
        });

        scanHandleRef.current = handle;
    };

    const handleScan = async (val: any, isOrder: boolean = false) => {
        if (isOrder) {
            let orderData:any = await findOrderById(val);

            if (typeof orderData == 'string') {
                return orderData;
            }

            if (orderData) {
                // Await the full scanner teardown - including the native MLKit
                // BarcodeScanner.stopScan() call, not just the CSS transparency toggle -
                // before navigating. useIonViewWillLeave fires stopScan() without
                // awaiting it, so without this, reportPage can mount and become
                // interactive while the native camera view is still up and possibly
                // intercepting touches, intermittently depending on device/camera speed.
                await stopScan();
                history.push(`${Links.report}/${orderData.id}/0/0/0/0/${internalCostReport}`);
            }
        } else {
            let orderData:any = await checkSpecificationFactory(val);

            if (typeof orderData == 'string') {
                return orderData;
            }

            let logisticContainerPrefix = orderData.scanning_settings.logisticContainerPrefix;
            logisticContainerPrefix = logisticContainerPrefix && logisticContainerPrefix.length ? logisticContainerPrefix : [0];

            if (orderData.logistic_container) {
              await stopScan();
              history.push(`${Links.qrCodeContainer}/${orderData.id}/${val}/${orderData.scanning_settings.logisticContainerShowAlerts}/${orderData.scanning_settings.logisticContainerUsePrefix}/${encodeURIComponent(logisticContainerPrefix.join(','))}/${internalCostReport}`);
            } else if (orderData) {
              await stopScan();
              history.push(`${Links.report}/${orderData.id}/${val}/0/0/0/${internalCostReport}`);
            }
        }
    }

    const start = async () => {
        let hasPermission = await checkCameraPermission();
        if (!hasPermission) {
            setShowPermissionAlert(true);
        }
        startScan();
        setShowError(false);
        setError('');
    }

    useIonViewWillEnter(() => {
        if (isPlatform('ios') || isPlatform('android')) {
            start();
        }
    });

    const stopScan = async () => {
        await scanHandleRef.current?.stop();
        scanHandleRef.current = null;
        restoreWebView();
    };

    useIonViewWillLeave(() => {
        if (isPlatform('ios') || isPlatform('android')) {
            stopScan();
        }
    });

    const updateHeight = () => {
        if (header.current?.clientHeight == 0) {
            setTimeout(updateHeight);
        } else {
            updateTopEdge(header.current?.clientHeight);
        }
    };

    const header = useRef<HTMLIonHeaderElement>(null);

    return (
        <IonPage>
            <StyledIonContentCameraPreview>
                <img className="background" src={CameraPreviewImage}/>
                <IonHeader ref={header} className="ion-no-border">
                    <StyledIonToolbar>
                        <IonButtons slot="start">
                            <StyledHeaderButton onClick={() => handleBack()}>
                                <StyledHeaderButtonImage src={ArrowLeftImage}></StyledHeaderButtonImage>
                            </StyledHeaderButton>
                        </IonButtons>
                        <StyledIonTitle>{t('reportsTab.orders.scanSpecificationPage.title')}</StyledIonTitle>
                    </StyledIonToolbar>
                    <div className="sub-title-light">{t('reportsTab.orders.scanSpecificationPage.subtitle')}</div>
                </IonHeader>
                <StyledContent>
                    <div className="target">
                        {
                            showError &&
                            <StyledEmptyContainer className="preview">
                                <CircleImage onClick={async (e) => {
                                    start();
                                }} image={ImageTypes.QR} color="red" />
                            </StyledEmptyContainer>
                        }
                    </div>
                    <div className={'action-buttons empty multiple-buttons'}>
                        <IonGrid>
                            {showError && <IonRow>
                                <IonCol size="12">
                                    <p className="errorMessage">{error}</p>
                                </IonCol>
                            </IonRow>}
                            <IonRow>
                                <IonCol size="6"><StyledButton onClick={() => history.push(Links.manualBarcode + '/' + internalCostReport)}>{t('reportsTab.orders.scanSpecificationPage.manualButton')}</StyledButton></IonCol>
                                <IonCol size="6"><StyledButton onClick={() => history.push(Links.manualOrderFind + '/' + internalCostReport)}>{t('manualOrderFind.find_button')}</StyledButton></IonCol>
                            </IonRow>
                        </IonGrid>
                    </div>
                </StyledContent>
            </StyledIonContentCameraPreview>
            <IonAlert
                isOpen={showPermissionAlert}
                onDidDismiss={() => setShowPermissionAlert(false)}
                header={t('common.permissions.alert')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowPermissionAlert(false);
                        },
                    },
                    {
                        text: t('common.permissions.goToAppSettings'),
                        handler: async () => {
                            setShowPermissionAlert(false);
                            openSettings();
                        }
                    }
                ]}
            />
        </IonPage>
    );
};

export default QRCodePage;
